import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Package, Target, ArrowRightLeft, Camera, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import {
  CollectionValueCard,
  ValueChart,
  TopMoversWidget,
  SetCompletionWidget,
  RecentActivityFeed,
  StatsCard,
  ActivityType
} from '../components/dashboard/index.ts';
import { getCardInstances, getCollectionStats, getRecentCards } from '../lib/collectionService.ts';
import { cardMarketConnector } from '../lib/marketConnectors.ts';
import { CardInstance, CollectionStats } from '../types/collection.ts';
import SearchModal from '../components/SearchModal.tsx';

interface PriceHistoryPoint {
  date: string;
  value: number;
}

interface CardMover {
  cardId: string;
  name: string;
  setName: string;
  imageUrl?: string;
  currentPrice: number;
  previousPrice: number;
  change: number;
  changePercent: number;
}

interface SetProgress {
  setId: string;
  name: string;
  ownedCount: number;
  totalCount: number;
  progress: number;
  isComplete: boolean;
}

interface Activity {
  id: string;
  type: ActivityType;
  cardId: string;
  cardName: string;
  setName: string;
  imageUrl?: string;
  price?: number;
  timestamp: Date;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryPoint[]>([]);
  const [gainers, setGainers] = useState<CardMover[]>([]);
  const [losers, setLosers] = useState<CardMover[]>([]);
  const [sets, setSets] = useState<SetProgress[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [previousValue, setPreviousValue] = useState<number | undefined>();
  const [searchOpen, setSearchOpen] = useState(false);

  const loadDashboardData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Load collection stats
      const collectionStats = await getCollectionStats(user.uid);
      setStats(collectionStats);

      // Load recent cards for activity feed
      const recentCards = await getRecentCards(user.uid, 10);
      const activityItems: Activity[] = recentCards.map((card: CardInstance) => ({
        id: card.id || card.cardId,
        type: 'added' as ActivityType,
        cardId: card.cardId,
        cardName: card.cardId.split('-')[1] || card.cardId, // Fallback name from ID
        setName: card.cardId.split('-')[0] || 'Unknown Set',
        timestamp: card.acquiredAt instanceof Date ? card.acquiredAt : new Date(),
      }));
      setActivities(activityItems);

      // Load all cards for value calculation
      const allCards = await getCardInstances(user.uid);

      // Calculate collection value from prices
      let totalValue = 0;
      const cardPrices: { cardId: string; price: number; previousPrice: number }[] = [];

      for (const card of allCards.slice(0, 50)) { // Limit to avoid too many API calls
        try {
          const priceData = await cardMarketConnector.getPriceData(card.cardId);
          if (priceData?.prices?.trendPrice) {
            const price = priceData.prices.trendPrice * card.quantity;
            totalValue += price;
            cardPrices.push({
              cardId: card.cardId,
              price: priceData.prices.trendPrice,
              previousPrice: priceData.prices.avg7 || priceData.prices.trendPrice * 0.95,
            });
          }
        } catch {
          // Skip cards with price errors
        }
      }

      // Update stats with calculated value
      if (stats) {
        setStats({ ...collectionStats, totalValue });
      }

      // Calculate top movers
      const movers = cardPrices.map(cp => {
        const change = cp.price - cp.previousPrice;
        const changePercent = (change / cp.previousPrice) * 100;
        return {
          cardId: cp.cardId,
          name: cp.cardId.split('-')[1] || cp.cardId,
          setName: cp.cardId.split('-')[0] || 'Unknown',
          currentPrice: cp.price,
          previousPrice: cp.previousPrice,
          change,
          changePercent,
        };
      });

      const sortedMovers = [...movers].sort((a, b) => b.changePercent - a.changePercent);
      setGainers(sortedMovers.filter(m => m.changePercent > 0).slice(0, 3));
      setLosers(sortedMovers.filter(m => m.changePercent < 0).reverse().slice(0, 3));

      // Generate mock price history (would come from Firestore in production)
      const history: PriceHistoryPoint[] = [];
      const today = new Date();
      let runningValue = totalValue * 0.85;

      for (let i = 89; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        runningValue *= 1 + (Math.random() - 0.48) * 0.02;
        history.push({
          date: date.toISOString().split('T')[0],
          value: runningValue,
        });
      }
      history.push({ date: today.toISOString().split('T')[0], value: totalValue });
      setPriceHistory(history);
      setPreviousValue(history[history.length - 31]?.value);

      // Load set progress (mock for now, would query Firestore)
      const mockSets: SetProgress[] = [
        { setId: 'sv3pt5', name: 'Pokemon 151', ownedCount: 94, totalCount: 165, progress: 0.57, isComplete: false },
        { setId: 'sv2', name: 'Paldea Evolved', ownedCount: 89, totalCount: 193, progress: 0.46, isComplete: false },
        { setId: 'sv3', name: 'Obsidian Flames', ownedCount: 158, totalCount: 197, progress: 0.80, isComplete: false },
      ];
      setSets(mockSets);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleCardClick = (cardId: string) => {
    navigate(`/card/${cardId}`);
  };

  const handleSetClick = (setId: string) => {
    navigate(`/set/${setId}`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 p-6">
        <div className="max-w-4xl mx-auto text-center py-20">
          <h1 className="text-4xl font-bold text-white mb-4">
            Pokemon TCG Collection Manager
          </h1>
          <p className="text-xl text-gray-400 mb-8">
            Track, manage, and grow your collection with real-time market prices
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <Camera className="w-10 h-10 text-blue-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Scan Cards</h3>
              <p className="text-gray-400 text-sm">
                Use your camera to quickly identify and add cards to your collection
              </p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <Target className="w-10 h-10 text-green-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Track Value</h3>
              <p className="text-gray-400 text-sm">
                Real-time pricing from CardMarket keeps your collection value up to date
              </p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <ArrowRightLeft className="w-10 h-10 text-purple-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Trade Cards</h3>
              <p className="text-gray-400 text-sm">
                Find traders who have what you need and need what you have
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              My Collection
            </h1>
            <p className="text-gray-400 mt-1">
              Track your Pokemon TCG portfolio
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/scan')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
            >
              <Camera className="w-4 h-4" />
              <span>Scan Card</span>
            </button>
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors"
            >
              <Search className="w-4 h-4" />
              <span>Search</span>
            </button>
          </div>
        </div>

        {/* Main Value Card */}
        <CollectionValueCard
          value={stats?.totalValue || 0}
          previousValue={previousValue}
          loading={loading}
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard
            title="Unique Cards"
            value={stats?.uniqueCards.toLocaleString() || '0'}
            subtitle={`${stats?.totalCards.toLocaleString() || '0'} total cards`}
            icon={Layers}
            iconColor="text-blue-400"
            loading={loading}
          />
          <StatsCard
            title="Sets Started"
            value={stats?.setsStarted || sets.length}
            subtitle={`${stats?.setsCompleted || 0} completed`}
            icon={Package}
            iconColor="text-green-400"
            loading={loading}
          />
          <StatsCard
            title="On Wishlist"
            value={stats?.cardsWanted || 0}
            subtitle="Cards you want"
            icon={Target}
            iconColor="text-yellow-400"
            loading={loading}
          />
          <StatsCard
            title="For Trade"
            value={stats?.cardsForTrade || 0}
            subtitle="Available to trade"
            icon={ArrowRightLeft}
            iconColor="text-purple-400"
            loading={loading}
          />
        </div>

        {/* Chart */}
        <ValueChart
          data={priceHistory}
          loading={loading}
          height={250}
        />

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Movers */}
          <TopMoversWidget
            gainers={gainers}
            losers={losers}
            loading={loading}
            onCardClick={handleCardClick}
          />

          {/* Set Completion */}
          <SetCompletionWidget
            sets={sets}
            loading={loading}
            onSetClick={handleSetClick}
          />
        </div>

        {/* Recent Activity */}
        <RecentActivityFeed
          activities={activities}
          loading={loading}
          onCardClick={handleCardClick}
        />
      </div>
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
